import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import type {TabItems, TabKey} from '@Components/tabs';
import {Tabs, useSelectedTab} from '@Components/tabs';
import type {CarouselData, SelectedFilter, StockButtonFilters, StockFilter, StockFilterID, StockPrimaryCTA} from '@Panels/stock-panel/stock-panel.types';
import {
  AudioFilters,
  DEFAULT_APPLIED_SUBFILTER,
  getAiSkeletonItem,
  getStockTabsToFilterMapping,
  MIN_STOCK_ITEMS_TO_DISPLAY,
  STOCK_PANEL_TAB_ID,
  STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP,
  STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING,
  StockCarouselIDs,
  StockCarouselType,
  StockPanelTabs,
} from '@Panels/stock-panel/stock-panel.types';
import {Text, TextSize, TextType} from '@Components/text';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {getRandomNumberInRange} from '@Utils/math.util';
import type {AITextToImageBackendData, AudioCategoryBackendData, GenerateAIImageResponse, GenerateImageParams, StockResponse} from '@Libraries/pmw-stock-media-library';
import {getStockForCarousel} from '@Libraries/pmw-stock-media-library';
import {
  AspectRatio,
  GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE,
  GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE,
  GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE,
  generateImage,
  getGeneratedImagesForCarousel,
  getRecentStockItems,
  getRemainingTextToImageUsages,
  getStockLibraryAudioCategories,
  setRecentStockItem,
  StockSources,
} from '@Libraries/pmw-stock-media-library';
import Carousel from '@Components/carousel/carousel';
import type {
  PMWStockBackendAiIcon,
  PMWStockBackendAiSticker,
  PMWStockBackendAnimatedSticker,
  PMWStockBackendExtractedGettySticker,
  PMWStockBackendIcon,
  PMWStockBackendShape,
  PMWStockBackendStillSticker,
} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import {
  createKeepFiltersHiddenForTab,
  FALLBACK_GRID_ITEM_HEIGHT,
  FALLBACK_GRID_ITEM_WIDTH,
  getBottomPills,
  getCarouselDataForAiIconItem,
  getCarouselDataForAiStickerItem,
  getCarouselDataForAudioItem,
  getCarouselDataForExtractedGettyStickerItem,
  getCarouselDataForIconItem,
  getCarouselDataForImageItem,
  getCarouselDataForShapeItem,
  getCarouselDataForStickerItem,
  getCarouselDataForStillStickerItem,
  getCarouselDataForVideoItem,
  getDefaultSelectedFilterForTab,
  getGATrackingFilterTerm,
  getGATrackingSearchTerm,
  getGATrackingSourceFromMediaSource,
  getGATrackingTypeFromMediaType,
  getGridID,
  getGridItemForAIGeneratedImageV2,
  getGridItemForBackendData,
  getHasUserSeenMobileVideoPreview,
  getHeightForCarouselType,
  getOptionsForStockGetter,
  getInitialOffsetsForInitialCarouselRequest,
  getPanelTitle,
  getRequiredSourcesForSelectedTab,
  getSearchBarPlaceholderText,
  getStockFilterForAudioFilterBackendData,
  getStockTabsOrder,
  getTabItem,
  GRID_SKELETON_LANDSCAPE_WIDTH,
  GRID_SKELETON_PORTRAIT_WIDTH,
  GRID_SKELETON_SQUARE_WIDTH,
  markUserHasSeenMobileVideoPreview,
  SHOULD_DISPLAY_AUDIO_ITEMS_IN_ALL_TAB_FOR_MOBILE,
  STOCK_AUDIO_GRID_ITEM_WIDTH,
  uploadSelectedItem,
} from '@Panels/stock-panel/stock-panel.library';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import type {BasePanel, PanelGrowlData} from '@Components/panel/panel.types';
import {PanelDataKeys} from '@Components/panel/panel.types';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import StockPanelFooter from '@Panels/stock-panel/components/stock-panel-footer';
import {SOURCE} from '@Libraries/stock-graphic-library';
import {validateGettyLicenseAgreement} from '@Panels/premium-agreement-panel/premium-agreement.library';
import {hideLoading, showLoading, updateLoadingText} from '@Libraries/loading-toast-library';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import type {ElementData} from '@Libraries/add-media-library';
import StockPanelSearchBar from '@Panels/stock-panel/components/stock-panel-search-bar';
import StockPanelGrid from '@Panels/stock-panel/components/stock-panel-grid';
import StockFilters from '@Panels/stock-panel/components/stock-filters';
import {setRecentStockAvailability, setStockGetterOptions, toggleFiltersVisibility} from '@Panels/stock-panel/stock-panel-reducer';
import {addItemsToGrid, clearSelectedItems, deleteGridItems, getSelectedGridItems, reloadGrid, replaceGridItem, toggleGridItemSelection} from '@Components/base-grid';
import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {deleteTabStateForId, selectTab} from '@Components/tabs/tabs-reducer';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {CarouselItemData} from '@Components/carousel/carousel.types';
import {TextToImageGrid} from '@Components/text-to-image-grid';
import type {GridStockImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {SkeletonAnimationType} from '@Components/base-grid/components/grid-skeleton-item';
import type {IconProps} from '@Components/icon-v2';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Size, Type} from '@Components/button';
import type {GridGenericButtonItemStorage} from '@Components/base-grid/components/grid-generic-button-item';
import {showTextToImageUpsellDialog} from '@Libraries/text-to-image-library';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {updateAiImageState, updateIsUploadingToMyUploads} from '@Components/poster-editor/poster-editor-reducer';
import {AiImageState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AnimatePresence, motion} from 'framer-motion';
import {ElementTestId} from '@Tests/tests.types';
import {pauseAllAudioGridItems} from '@Components/base-grid/components/grid-user-audio-item/grid-audio-item-reducer';
import {isMobile} from 'react-device-detect';
import useWindowSize from '@Hooks/useWindowSize';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './stock-panel.module.scss';
import {updatePanelData} from '@Components/panel/panel-reducer';
import {AiImageModelOption, MediaTypePrompt, TextToImageGridErrorState} from '@Panels/ai-image-panel/ai-image-panel.types';
import type {Dimensions} from '@floating-ui/react';
import AiImagePromptBox from '@Panels/ai-image-panel/components/ai-image-prompt-box/ai-image-prompt-box';
import type {SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {PromptOption} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {v4 as uuidv4} from 'uuid';

const MORE_OPTIONS_TABS_THRESHOLD = 1050;

const MOBILE_VIDEO_PREVIEW_GROWL_VISIBLE_DURATION = 3000;

export interface StockPanelProps extends BasePanel {
  preSelectedFilters?: SelectedFilter[];
  keepFiltersHiddenForTab?: Record<StockPanelTabs, boolean>;
  bottomPills?: PanelBottomPill[];
  tabsToShow?: StockPanelTabs[];
  defaultSearchTab?: StockPanelTabs;
  maxSelectionNumber?: number;
  selectionID?: string;
  addInBulk?: boolean;
  shouldLoadDataUrlImage?: boolean;
  showTextToImageTool?: boolean;
  populateRecentlyAddedOnItemAdd?: boolean;
  videoQualityFiltersDisabled?: boolean;
  tabId?: string;
  stockTabToGridIdMapping?: Record<StockPanelTabs, string>;
  onStartAddSelected?: () => void;
  isManageappPage?: boolean;
  blacklistedSources?: StockSources[];
  bypassCompoundedView?: boolean;

  onAddSelected(items: ElementData[], currentUploadIndex?: number, totalUploadsCount?: number): void | Promise<void>;

  onBack?(): void;

  onClose?(): void;
}

const [MIN_SKELETON_WIDTH, MAX_SKELETON_WIDTH] = [80, 220];
const TOTAL_SKELETONS_PER_ROW = 10;
const DEFAULT_SELECTION_ID = 'STOCK_MEDIA_MODAL_SELECTION_ID';
const query: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 640,
  },
  large: {
    minWidth: 641,
  },
};
const MIN_NUMBER_OF_BOTTOM_PILLS_TO_SHOW = 2;
const CAROUSEL_BUTTON_ITEM_WIDTH = 264;

const AI_ICON_PROPS: IconProps = {
  icon: 'icon-ai-image',
  size: IconSize.SIZE_ICON_20,
  className: `${styles.aiIconStyle} spacing-m-r-1`,
};

export default function StockPanel({
  populateRecentlyAddedOnItemAdd = true,
  showTextToImageTool = false,
  tabsToShow = getStockTabsOrder(!!showTextToImageTool),
  videoQualityFiltersDisabled = false,
  onStartAddSelected = (): void => {},
  tabId = STOCK_PANEL_TAB_ID,
  preSelectedFilters = [],
  keepFiltersHiddenForTab = createKeepFiltersHiddenForTab(),
  isManageappPage = false,
  bypassCompoundedView = false,

  ...props
}: StockPanelProps): ReactElement<any> {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(bypassCompoundedView || tabsToShow.length === 1 ? '' : undefined);
  const panelId = props.panelId ?? 'STOCK_MEDIA_PANEL_NO_ID_PASSED';
  const selectedTab = useSelectedTab(tabId, props.defaultSearchTab ?? tabsToShow[0]) as StockPanelTabs;
  const stockTabsToFilterMapping = useRef<Record<StockPanelTabs, StockFilter[]>>(getStockTabsToFilterMapping(videoQualityFiltersDisabled));
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>(preSelectedFilters);
  const [loaded, setLoaded] = useState(false);
  const [recentStockLoaded, setRecentStockLoaded] = useState(false);
  const [generatedImagesLoaded, setGeneratedImagesLoaded] = useState(false);
  const [errorState, setErrorState] = useState<TextToImageGridErrorState>(TextToImageGridErrorState.NONE);
  const [renderFilterSubFilters, setRenderFilterSubFilters] = useState<string | undefined>(undefined);
  const carouselData = useRef<CarouselData>({});
  const panelContentRef = useRef<HTMLDivElement>(null);
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const stockSubfilterToAdditionalFiltersMapping = useRef(STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP);
  const audioFilterIDs = useRef<(StockButtonFilters | string)[]>(AudioFilters);
  const [remainingTextToImageUsages, setRemainingTextToImageUsages] = useState<number>(0);
  const [mediaTypePrompt, setMediaTypePrompt] = useState<MediaTypePrompt>(MediaTypePrompt.PHOTO);
  const lastSearchTerm = useRef<string | undefined>(undefined);
  const aiSkeletonItemIds = useRef<string[]>([]);

  const clearSelection = (): void => {
    dispatch(
      clearSelectedItems({
        gridSelectionGroupId: carouselItemsSelectionID.current,
      })
    );
  };
  const onHide = (): void => {
    clearSelection();
    dispatch(pauseAllAudioGridItems());
  };
  const onClose = props.onClose?.bind(null) ?? useDefaultPanelClose(panelId, props.hideOnClose, onHide);
  const carouselItemsSelectionID = useRef(props.selectionID ?? DEFAULT_SELECTION_ID);
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const areFiltersVisible = useAppSelector((state) => {
    return state.stockPanel.areFiltersVisible;
  });
  const isRecentStockAvailble = useAppSelector((state) => {
    return state.stockPanel.isRecentStockAvailable;
  });
  const aiImageState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImage;
  });
  const params = useContainerQuery(panelContentRef, query);
  const dispatch = useAppDispatch();
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, carouselItemsSelectionID.current);
  });
  const AI_IMAGE_BUTTON_GRID_ITEM: GridGenericButtonItemStorage = {
    type: GRID_ITEM_TYPE.GENERIC_BUTTON,
    id: StockCarouselType.TEXT_TO_IMAGE,
    buttonProps: {
      text: window.i18next.t('pmwjs_generate_your_own'),
      customClasses: `hover-transition-all ${styles.aiCarouselButtonItem}`,
      type: Type.PRIMARY,
      icon: 'icon-ai-image',
      size: Size.XSMALL,
    },
    containerClassName: `${styles.aiCarouselButtonItemContainer} radius-12`,
  };
  const {windowWidth} = useWindowSize();
  const selectedAiModelId = useAppSelector((state) => {
    return state.userPreferences.selectedAiImageModelId;
  });

  useEffect(() => {
    getRemainingTextToImageInvocations();
  }, []);

  const getRemainingTextToImageInvocations = (): void => {
    getRemainingTextToImageUsages()
      .then((res) => {
        setRemainingTextToImageUsages(res);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const doesFiltersVisibilityNeedToggling = (tabKey: TabKey): boolean => {
    const filtersForTab: StockFilter[] = stockTabsToFilterMapping.current[tabKey as StockPanelTabs];
    return (filtersForTab.length <= 0 && areFiltersVisible) || (filtersForTab.length > 0 && !areFiltersVisible);
  };

  const toggleFiltersVisibilityIfChanged = (tabKey: TabKey): void => {
    if (doesFiltersVisibilityNeedToggling(tabKey)) {
      toggleFilters();
    }
  };

  const onAIImageCarouselButtonClicked = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_AI_IMAGE_GENERATE_YOUR_OWN);
    setSearchTerm('');
    onTabClicked(StockPanelTabs.TEXT_TO_IMAGE);
  };

  const AI_IMAGE_BUTTON_CAROUSEL_ITEM: CarouselItemData = {
    gridItemData: {...AI_IMAGE_BUTTON_GRID_ITEM, onClicked: onAIImageCarouselButtonClicked},
    width: CAROUSEL_BUTTON_ITEM_WIDTH,
    height: getHeightForCarouselType(StockCarouselType.TEXT_TO_IMAGE, params.small),
  };

  const shouldShowBackButton = (): boolean => {
    return (isGridView() && tabsToShow.length > 1) || !!props.onBack;
  };

  const onBackButton = (): void => {
    if ((!isGridView() || tabsToShow.length === 1) && !!props.onBack) {
      props.onBack();
    } else {
      dispatch(selectTab(tabId, props.defaultSearchTab ?? tabsToShow[0]));
      setSelectedFilters([]);
      setSearchTerm(undefined);
    }
  };

  const getHeaderLeftIconProps = (): IconProps | undefined => {
    if (selectedTab === StockPanelTabs.TEXT_TO_IMAGE) {
      return AI_ICON_PROPS;
    }
    return undefined;
  };

  const getHeader = (): ReactElement<any> => {
    return (
      <PanelHeader
        title={getPanelTitle(selectedTab, searchTerm)}
        titleClasses={selectedTab === StockPanelTabs.TEXT_TO_IMAGE ? styles.aiImageTitleText : undefined}
        centerAlignTitle={params.small}
        onClose={onClose}
        showCloseButton
        showBackButton={shouldShowBackButton()}
        onBackButton={onBackButton}
        leftIconProps={getHeaderLeftIconProps()}
      />
    );
  };

  const onCTA: StockPrimaryCTA = async (items): Promise<void> => {
    onClose();
    onStartAddSelected();

    const isAnySourceGetty = items.some((item) => {
      return item.source === SOURCE.GETTY;
    });

    let gettyAgreement = false;

    if (isAnySourceGetty) {
      gettyAgreement = await validateGettyLicenseAgreement();
    }

    showLoading('addStockMediaItem', {
      text: `${window.i18next.t('pmwjs_adding')} 1 of ${items.length}`,
    });

    const allItems = [];
    let recentStockItemCount = 0;

    for (let i = 0; i < items.length; i++) {
      if (items[i].source === SOURCE.GETTY && !gettyAgreement) {
        continue;
      }

      updateLoadingText('addStockMediaItem', `${window.i18next.t('pmwjs_adding')} ${i + 1} of ${items.length}`);
      trackPosterBuilderGA4Events(`MediaModal_${getGATrackingSourceFromMediaSource(items[i].source as SOURCE) + getGATrackingTypeFromMediaType(items[i].type)}Add`);

      try {
        if (items[i].source === SOURCE.AI_TEXT_TO_IMAGE) {
          dispatch(updateIsUploadingToMyUploads(true));
        }

        const onAddItemResponse = await uploadSelectedItem(items[i], props.shouldLoadDataUrlImage);

        dispatch(updateIsUploadingToMyUploads(false));

        if (!props.addInBulk) {
          await props.onAddSelected([onAddItemResponse], i + 1, items.length);
        } else {
          allItems.push(onAddItemResponse);
        }

        if (populateRecentlyAddedOnItemAdd && isUserLoggedIn.current) {
          recentStockItemCount = await setRecentStockItem(items[i]);
        }
      } catch (e) {
        console.error(e);

        openModal(MODAL_IDS.ERROR_MODAL_ID, {
          message: window.i18next.t('pmwjs_general_ajax_error'),
          log: false,
        });
      }
    }

    if (props.addInBulk) {
      await props.onAddSelected(allItems);
    }

    dispatch(setRecentStockAvailability(recentStockItemCount >= MIN_STOCK_ITEMS_TO_DISPLAY));
    hideLoading('addStockMediaItem');
  };

  // CodeReviewUsama: Have a separate state for identifying whether it's the carousels view or not rather than using searchTerm undefined for it.
  const shouldShowBottomPills = (bottomPillsLength: number): boolean => {
    return (isGridView() && params.small && bottomPillsLength >= MIN_NUMBER_OF_BOTTOM_PILLS_TO_SHOW) || !!props.bottomPills;
  };

  const getFooter = (): ReactElement<any> => {
    const bottomPills = getPanelBottomPills();

    return (
      <StockPanelFooter
        isMobile={params.small}
        onPrimaryAction={onCTA}
        selectionGroupID={carouselItemsSelectionID.current}
        bottomPillItems={shouldShowBottomPills(bottomPills.length) ? bottomPills : undefined}
        makeBottomPillsScrollable={!props.bottomPills}
      />
    );
  };

  const getTabItems = (): TabItems => {
    return tabsToShow.map((tab) => {
      return {
        ...getTabItem(tab, tab === StockPanelTabs.TEXT_TO_IMAGE ? styles.aiImageTitleText : undefined),
        ...(tab === StockPanelTabs.TEXT_TO_IMAGE ? {preTitleContent: <Icon {...AI_ICON_PROPS} />} : {}),
      };
    });
  };

  const getGridIdForTab = (tab: StockPanelTabs): string => {
    if (props.stockTabToGridIdMapping) {
      return props.stockTabToGridIdMapping[tab];
    }
    return getGridID(tab);
  };

  const onTabClicked = (tabKey: TabKey): void => {
    const currentGridID = getGridIdForTab(selectedTab);

    if (selectedTab !== tabKey) {
      dispatch(
        reloadGrid({
          gridId: currentGridID,
          gridSelectionGroupId: carouselItemsSelectionID.current,
        })
      );
      dispatch(selectTab(tabId, tabKey));
      toggleFiltersVisibilityIfChanged(tabKey);
      setSelectedFilters(getDefaultSelectedFilterForTab(tabKey as StockPanelTabs));
      dispatch(setStockGetterOptions({}));
    }
  };

  const shouldShowClearAllFilters = (): boolean => {
    return !areFiltersVisible && selectedFilters.length > 0;
  };

  const showMoreOptionsInTabs = (): boolean => {
    return windowWidth < MORE_OPTIONS_TABS_THRESHOLD;
  };

  const getTabs = (): ReactElement<any> => {
    return (
      <div className={`_full-width flex-justify-between flex-row-align-center spacing-m-t-8 `}>
        <Tabs
          id={tabId}
          moreOptionsIndex={showMoreOptionsInTabs() ? 5 : undefined}
          tabItems={getTabItems()}
          selectedTab={selectedTab}
          onTabClick={onTabClicked}
          className="spacing-m-b-0 spacing-p-0"
        />
        {shouldShowClearAllFilters() ? (
          <Text
            onClick={(): void => {
              setSelectedFilters([]);
            }}
            className={`${styles.clearAllFiltersText} content-link`}
            val={window.i18next.t('pmwjs_clear_filters')}
            size={TextSize.XSMALL}
            underline
          />
        ) : null}
      </div>
    );
  };

  const toggleFilters = (): void => {
    dispatch(toggleFiltersVisibility());
    if (renderFilterSubFilters) {
      setRenderFilterSubFilters(undefined);
    }
  };

  const getAdditionalFilters = (tab: StockPanelTabs, filterID?: string, subfilterID?: string): StockFilter[] => {
    if (!subfilterID || !filterID) {
      return [];
    }
    if (stockSubfilterToAdditionalFiltersMapping.current[tab][filterID]) {
      return stockSubfilterToAdditionalFiltersMapping.current[tab][filterID][subfilterID] ?? [];
    }
    return [];
  };

  const onGeneratedImagesReceived = (response: AITextToImageBackendData[]): void => {
    const items: GridStockImageItemStorage[] = response.map(getGridItemForAIGeneratedImageV2);

    items.forEach((gridItem): void => {
      const skeletonId = aiSkeletonItemIds.current.shift();
      if (skeletonId) {
        dispatch(replaceGridItem({gridId: getGridIdForTab(selectedTab), itemId: skeletonId, gridItem}));
      }
    });

    setErrorState(TextToImageGridErrorState.NONE);
  };

  const getDimensionsForAiImageSkeletonItem = (aspectRatio: string): Partial<GridSkeletonItemStorage> => {
    switch (aspectRatio) {
      case AspectRatio.PORTRAIT:
        return {width: GRID_SKELETON_PORTRAIT_WIDTH};
      case AspectRatio.LANDSCAPE:
        return {width: GRID_SKELETON_LANDSCAPE_WIDTH};
      case AspectRatio.SQUARE:
      default:
        return {width: GRID_SKELETON_SQUARE_WIDTH};
    }
  };

  const onAIImageGenerateSuccess = (generateImagesResponse: GenerateAIImageResponse): void => {
    onGeneratedImagesReceived(generateImagesResponse.generatedImages);
    setRemainingTextToImageUsages(generateImagesResponse.remainingCredits);
    setErrorState(TextToImageGridErrorState.NONE);
  };

  const addLoadingSkeletons = (aspectRatio: string): void => {
    const skeletonItemIds = [uuidv4(), uuidv4()];
    const items = [
      {...getAiSkeletonItem(skeletonItemIds[0]), ...getDimensionsForAiImageSkeletonItem(aspectRatio), animationType: SkeletonAnimationType.AI},
      {...getAiSkeletonItem(skeletonItemIds[1]), ...getDimensionsForAiImageSkeletonItem(aspectRatio), animationType: SkeletonAnimationType.AI},
    ];
    dispatch(
      addItemsToGrid({
        id: getGridIdForTab(StockPanelTabs.TEXT_TO_IMAGE),
        index: 0,
        items: items,
      })
    );
    aiSkeletonItemIds.current.push(...skeletonItemIds);
  };

  const removeLoadingSkeletons = (): void => {
    dispatch(
      deleteGridItems({
        gridId: getGridIdForTab(StockPanelTabs.TEXT_TO_IMAGE),
        gridSelectionGroupId: carouselItemsSelectionID.current,
        itemIdsToDelete: aiSkeletonItemIds.current.slice(2),
      })
    );
    aiSkeletonItemIds.current.shift();
    aiSkeletonItemIds.current.shift();
  };

  const handleInsufficientCredits = (prompt: string, promptOptions: SelectedPromptOptionValues): void => {
    removeLoadingSkeletons();

    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase((data) => {
        if (data.credits > 0) {
          void onAIImageGenerate(prompt, promptOptions);
        }
      });
    });
  };

  const onAIImageGenerateError = (e: Error, prompt: string, promptOptions: SelectedPromptOptionValues): void => {
    setErrorState((): TextToImageGridErrorState => {
      if (e.message === GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE) {
        handleInsufficientCredits(prompt, promptOptions);
        return TextToImageGridErrorState.NONE;
      }
      if (e.message === GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE) {
        return TextToImageGridErrorState.FLAGGED;
      }
      if (e.message === GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE) {
        return TextToImageGridErrorState.VAGUE;
      }
      if (e.message === GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE) {
        return TextToImageGridErrorState.UNSUPPORTED_LANUAGE;
      }
      return TextToImageGridErrorState.GENERIC;
    });

    removeLoadingSkeletons();
  };

  const onAIImageGenerate = async (prompt: string, promptOptions: SelectedPromptOptionValues): Promise<void> => {
    if (!isPremium.current) {
      void showTextToImageUpsellDialog();
      return;
    }

    if (prompt.length) {
      setErrorState(TextToImageGridErrorState.NONE);
      dispatch(updateAiImageState(AiImageState.GENERATING));
      let aspectRatio: string = AspectRatio.SQUARE;
      let selectedAIImageStyle = '';
      const promptOptionsKeys = Object.keys(promptOptions) as PromptOption[];
      promptOptionsKeys.forEach((key: PromptOption) => {
        if (key === PromptOption.ASPECT_RATIO) {
          aspectRatio = promptOptions[key];
        }
        if (key === PromptOption.STYLES) {
          selectedAIImageStyle = promptOptions[key];
        }
      });
      addLoadingSkeletons(aspectRatio);
      trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_AI_IMAGE_GENERATE, {
        [GA4EventParamName.TYPE]: selectedAIImageStyle ?? '',
      });
      lastSearchTerm.current = prompt;
      const generateImageParams: GenerateImageParams = {
        prompt: prompt,
        style: selectedAIImageStyle,
        aspectRatio: aspectRatio,
        providerId: selectedAiModelId !== AiImageModelOption.Auto ? selectedAiModelId : undefined,
      };
      try {
        const generateImageResponse = await generateImage(generateImageParams);
        onAIImageGenerateSuccess(generateImageResponse);
      } catch (e) {
        if (e instanceof Error) {
          onAIImageGenerateError(e, prompt, promptOptions);
        } else {
          setErrorState(TextToImageGridErrorState.GENERIC);
          removeLoadingSkeletons();
        }
      } finally {
        dispatch(updateAiImageState(AiImageState.READY));
        setTimeout(() => {
          dispatch(updateAiImageState(AiImageState.IDLE));
        }, 2000);
      }
    }
  };

  const getSearchBarContainer = (): ReactElement<any> => {
    if (isTextToImageTabActive()) {
      return (
        <AiImagePromptBox
          generateImage={onAIImageGenerate}
          errorState={errorState}
          remainingCredits={remainingTextToImageUsages}
          mediaTypePrompt={mediaTypePrompt}
          updateMediaTypePrompt={setMediaTypePrompt}
        />
      );
    }
    return (
      <StockPanelSearchBar
        showCrossIcon={typeof searchTerm !== 'undefined'}
        className={params.small ? 'spacing-m-b-2' : ''}
        onSearch={(updatedSearchTerm) => {
          setSearchTerm((prevState) => {
            lastSearchTerm.current = prevState;
            return updatedSearchTerm;
          });
        }}
        placeholderText={getSearchBarPlaceholderText(isGridView() ? selectedTab : undefined)}
        inputFieldStyles={styles.searchBarHeightStandard}
        val={searchTerm}
      />
    );
  };

  const shouldAddDividersBetweenFilters = (): boolean => {
    return selectedTab !== StockPanelTabs.AUDIOS && selectedTab !== StockPanelTabs.ICONS && !videoQualityFiltersDisabled;
  };

  const getFiltersRow = (): ReactElement<any> => {
    const filtersForTab = stockTabsToFilterMapping.current[selectedTab];
    const additionalFilters = selectedFilters
      .map((selectedFilter) => {
        return getAdditionalFilters(selectedTab, selectedFilter.filter, selectedFilter.subFilter ?? undefined);
      })
      .flat();

    return (
      <StockFilters
        pillSize={params.small ? PillButtonSize.SMALL : PillButtonSize.MEDIUM}
        isVisible={areFiltersVisible && !keepFiltersHiddenForTab[selectedTab]}
        onFilterItemClicked={onFilterChanged}
        selectedFilters={selectedFilters}
        items={[...filtersForTab, ...additionalFilters]}
        className={`spacing-m-b-2 ${params.small ? 'spacing-m-t-2' : 'spacing-m-t-5'}`}
        addDividers={shouldAddDividersBetweenFilters()}
        isMobile={params.small}
        selectedTab={selectedTab}
      />
    );
  };

  // CodeReviewUsama: Use undfined instead of null. In ts usage of null isn't recommended
  const onFilterChanged = (filterID: StockFilterID, subFilterID: string | null): void => {
    let requiredFilters = selectedFilters.filter((selectedFilter) => {
      return selectedFilter.filter !== filterID.valueOf();
    });

    if (subFilterID !== null && subFilterID !== DEFAULT_APPLIED_SUBFILTER.id) {
      requiredFilters.push({
        filter: filterID,
        subFilter: subFilterID,
      });
    }

    const filterIDsToRemove: StockFilterID[] = Object.keys(stockSubfilterToAdditionalFiltersMapping.current[selectedTab][filterID] ?? [])
      .map((key) => {
        return stockSubfilterToAdditionalFiltersMapping.current[selectedTab][filterID][key].map((stockFilter) => {
          return stockFilter.id;
        });
      })
      .flat();

    requiredFilters = requiredFilters.filter((selectedfilter) => {
      return !filterIDsToRemove.includes(selectedfilter.filter as StockFilterID);
    });

    setSelectedFilters(requiredFilters);
  };

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    if (isDoubleClick && selectedItems.length === 1) {
      void onCTA(selectedItems);
    } else {
      dispatch(
        toggleGridItemSelection({
          gridItemId: item.id,
          gridSelectionGroupId: carouselItemsSelectionID.current,
        })
      );
    }
  };

  const getAIImageGrid = (gridItemID: string): ReactElement<any> => {
    const gridProps: TextToImageGridProps = {
      customOnGridItemClicked: onGridItemClicked,
      gridClasses: `spacing-p-t-6 spacing-p-b-6 spacing-p-r-3 spacing-p-l-3 spacing-m-t-2`,
      gridSelectionGroupId: carouselItemsSelectionID.current,
      maxSelectionNumber: props.maxSelectionNumber,
      gridId: gridItemID,
      isLoading: aiImageState === AiImageState.GENERATING,
    };

    return <TextToImageGrid key={gridItemID} {...gridProps} />;
  };

  const getCustomDimensionForGridItems = (): Dimensions | undefined => {
    if (selectedTab === StockPanelTabs.AUDIOS) {
      return {
        width: 250,
        height: 80,
      };
    }
    if (selectedTab === StockPanelTabs.SHAPES) {
      return {
        width: 110,
        height: 110,
      };
    }
    return;
  };

  const getStockGrid = (): ReactElement<any> => {
    let sourcesToGet =
      props.blacklistedSources && props.blacklistedSources.length > 0
        ? getRequiredSourcesForSelectedTab(selectedTab, selectedFilters).filter((source) => {
            return !props.blacklistedSources?.includes(source);
          })
        : getRequiredSourcesForSelectedTab(selectedTab, selectedFilters);
    if (!SHOULD_DISPLAY_AUDIO_ITEMS_IN_ALL_TAB_FOR_MOBILE && selectedTab === StockPanelTabs.ALL && isMobile) {
      sourcesToGet = sourcesToGet.filter((src: StockSources) => {
        return src !== StockSources.STORYBLOCK_AUDIO;
      });
    }
    const gridItemID = getGridIdForTab(selectedTab);

    if (selectedTab === StockPanelTabs.TEXT_TO_IMAGE) {
      return getAIImageGrid(gridItemID);
    }

    if (selectedTab === StockPanelTabs.STICKERS && isManageappPage) {
      sourcesToGet = sourcesToGet.filter((source) => {
        return source !== StockSources.PMW_EXTRACTED_GETTY_STICKER;
      });
    }

    if (selectedTab === StockPanelTabs.PHOTOS && searchTerm?.length === 0) {
      sourcesToGet = sourcesToGet.filter((source) => {
        return source !== StockSources.PMW_STOCK_IMAGE;
      });
    }

    return (
      <StockPanelGrid
        isManageappPage={isManageappPage}
        key={gridItemID}
        onSingleItemDoubleClick={onCTA}
        searchTerm={searchTerm}
        selectionGroupID={carouselItemsSelectionID.current}
        maxSelectionNumber={props.maxSelectionNumber}
        gridID={gridItemID}
        sources={sourcesToGet}
        generateImageCallback={(): void => {
          onTabClicked(StockPanelTabs.TEXT_TO_IMAGE);
        }}
        gridItemDimensions={getCustomDimensionForGridItems()}
      />
    );
  };

  const getLoadingSkeletonRow = (carouselType: StockCarouselType): ReactElement<any>[] => {
    const skeletons: ReactElement<any>[] = [];
    for (let i = 0; i < TOTAL_SKELETONS_PER_ROW; i++) {
      const randomWidth = getRandomNumberInRange(MIN_SKELETON_WIDTH, MAX_SKELETON_WIDTH);
      skeletons.push(
        <li key={i}>
          <SkeletonBrick className="radius-12" height={getHeightForCarouselType(carouselType, params.small)} width={randomWidth} />
        </li>
      );
    }

    return skeletons;
  };

  const getLoadingSkeletonForCarousel = (type: StockCarouselType): ReactElement<any> => {
    return <ul className={`${styles.skeletonList} spacing-m-0 flex-h-row spacing-p-t-4`}>{getLoadingSkeletonRow(type)}</ul>;
  };

  const onViewAllClicked = (carouselType: StockCarouselType): void => {
    switch (carouselType) {
      case StockCarouselType.AUDIOS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.AUDIOS));
        dispatch(selectTab(tabId, StockPanelTabs.AUDIOS));
        break;
      case StockCarouselType.PHOTOS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.PHOTOS));
        dispatch(selectTab(tabId, StockPanelTabs.PHOTOS));
        break;
      case StockCarouselType.SHAPES:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.SHAPES));
        dispatch(selectTab(tabId, StockPanelTabs.SHAPES));
        break;
      case StockCarouselType.STICKERS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.STICKERS));
        dispatch(selectTab(tabId, StockPanelTabs.STICKERS));
        break;
      case StockCarouselType.ICONS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.ICONS));
        dispatch(selectTab(tabId, StockPanelTabs.ICONS));
        break;
      case StockCarouselType.VIDEOS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.VIDEOS));
        dispatch(selectTab(tabId, StockPanelTabs.VIDEOS));
        break;
      case StockCarouselType.TEXT_TO_IMAGE:
        trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_AI_IMAGE_VIEW_ALL);
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.TEXT_TO_IMAGE));
        dispatch(selectTab(tabId, StockPanelTabs.TEXT_TO_IMAGE));
        break;
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
    setSearchTerm('');
  };

  const shouldShowShowMoreCTA = (carouselType: StockCarouselType): boolean => {
    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return false;
      case StockCarouselType.TEXT_TO_IMAGE:
      case StockCarouselType.ICONS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.STICKERS:
      case StockCarouselType.VIDEOS:
        return true;
      default:
        throw new Error(`Unhanled carousel type: ${carouselType}`);
    }
  };

  const getTextForCarouselLabel = (carouselType: StockCarouselType): ReactElement<any> => {
    switch (carouselType) {
      case StockCarouselType.STICKERS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.ICONS:
      case StockCarouselType.RECENTS:
      case StockCarouselType.VIDEOS:
        return <Text val={getCarouselTitleText(carouselType)} className="content-body" type={TextType.BODY} size={TextSize.XSMALL} bold />;
      case StockCarouselType.TEXT_TO_IMAGE:
        return (
          <div className={`flex-align-items-center flex-h-row ${styles.textToImageCarouselTitleContainer}`}>
            <Icon icon="icon-ai-image" size={IconSize.SIZE_ICON_16} className={styles.aiIconStyle} />
            <Text val={getCarouselTitleText(carouselType)} className={styles.aiImageTitleText} type={TextType.BODY} size={TextSize.XSMALL} bold />
          </div>
        );
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
  };

  const getCarouselUIForCarouselContent = (carouselContentMarkup: ReactElement<any>, carouselType: StockCarouselType): ReactElement<any> => {
    return (
      <li className={`${styles.carouselRow} spacing-p-0 flex-v-row`} key={carouselType} data-testid={getTestIdForCarouselType(carouselType)}>
        <div className="flex-h-row flex-justify-between">
          {getTextForCarouselLabel(carouselType)}
          {shouldShowShowMoreCTA(carouselType) ? (
            <Text
              className={`${styles.viewAllText} test-view-all`}
              val={window.i18next.t('pmwjs_carousel_view_all')}
              type={TextType.BODY}
              size={TextSize.XSMALL}
              onClick={(): void => {
                onViewAllClicked(carouselType);
              }}
              bold
            />
          ) : null}
        </div>
        {carouselContentMarkup}
      </li>
    );
  };

  const getTestIdForCarouselType = (carouselType: StockCarouselType): ElementTestId | undefined => {
    switch (carouselType) {
      case StockCarouselType.SHAPES:
        return ElementTestId.STOCK_PANEL_CAROUSEL_SHAPES;
      default:
        return undefined;
    }
  };

  const getCarouselTitleText = (carouselType: StockCarouselType): string => {
    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return window.i18next.t('pmwjs_recent');
      case StockCarouselType.PHOTOS:
        return window.i18next.t('pmwjs_photos_2');
      case StockCarouselType.STICKERS:
        return window.i18next.t('pmwjs_stickers');
      case StockCarouselType.SHAPES:
        return window.i18next.t('pmwjs_shapes');
      case StockCarouselType.VIDEOS:
        return window.i18next.t('pmwjs_videos_2');
      case StockCarouselType.AUDIOS:
        return window.i18next.t('pmwjs_audio');
      case StockCarouselType.ICONS:
        return window.i18next.t('pmwjs_icons');
      case StockCarouselType.TEXT_TO_IMAGE:
        return window.i18next.t('pmwjs_ai_images');
      default:
        throw new Error(`Unhandled carousel type for getCarouselTitleText: ${carouselType}`);
    }
  };

  const shouldShowLoadingCarouselForCarouselType = (carouselType: StockCarouselType): boolean => {
    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return !recentStockLoaded;
      case StockCarouselType.VIDEOS:
      case StockCarouselType.STICKERS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.ICONS:
        return !loaded;
      case StockCarouselType.TEXT_TO_IMAGE:
        return !generatedImagesLoaded;
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
  };

  const getCarouselViewRow = (carouselType?: StockCarouselType): ReactElement<any> | null => {
    if (carouselType) {
      if (shouldShowLoadingCarouselForCarouselType(carouselType)) {
        return getCarouselUIForCarouselContent(getLoadingSkeletonForCarousel(carouselType), carouselType);
      }
      const carousel = getCarouselForType(carouselType);
      return carousel ? getCarouselUIForCarouselContent(carousel, carouselType) : null;
    }
    return null;
  };

  const getCarouselForType = (carouselType: StockCarouselType): ReactElement<any> | null => {
    const commonProps = {
      selectionGroupId: carouselItemsSelectionID.current,
      height: getHeightForCarouselType(carouselType, params.small),
      nextBtnRightPositioning: params.small ? -8 : undefined,
      prevBtnLeftPositioning: params.small ? -8 : undefined,
      maxSelectionNumber: props.maxSelectionNumber,
    };

    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return carouselData.current[StockCarouselType.RECENTS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.RECENTS} items={carouselData.current[StockCarouselType.RECENTS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.PHOTOS:
        return carouselData.current[StockCarouselType.PHOTOS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.PHOTOS} items={carouselData.current[StockCarouselType.PHOTOS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.STICKERS:
        return carouselData.current[StockCarouselType.STICKERS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.STICKERS} items={carouselData.current[StockCarouselType.STICKERS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.SHAPES:
        return carouselData.current[StockCarouselType.SHAPES] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.SHAPES} items={carouselData.current[StockCarouselType.SHAPES]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.ICONS:
        return carouselData.current[StockCarouselType.ICONS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.ICONS} items={carouselData.current[StockCarouselType.ICONS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.VIDEOS:
        return carouselData.current[StockCarouselType.VIDEOS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.VIDEOS} items={carouselData.current[StockCarouselType.VIDEOS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.AUDIOS:
        return carouselData.current[StockCarouselType.AUDIOS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.AUDIOS} items={carouselData.current[StockCarouselType.AUDIOS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.TEXT_TO_IMAGE:
        return carouselData.current[StockCarouselType.TEXT_TO_IMAGE] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.TEXT_TO_IMAGE} items={carouselData.current[StockCarouselType.TEXT_TO_IMAGE]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      default:
        throw new Error(`Unhandled StockCarouselType: ${carouselType}`);
    }
  };

  const getCarouselsForTabs = (): (ReactElement<any> | null)[] => {
    const carouselRow: (ReactElement<any> | null)[] = [];
    tabsToShow.forEach((tab) => {
      let carouselsToShowForTab = STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING[tab];
      if (!isRecentStockAvailble) {
        carouselsToShowForTab = carouselsToShowForTab?.filter((carouselType) => {
          return carouselType !== StockCarouselType.RECENTS;
        });
      }
      const carousels = carouselsToShowForTab?.map(getCarouselViewRow);
      carousels?.forEach((carouselMarkdown) => {
        carouselRow.push(carouselMarkdown);
      });
    });
    return carouselRow;
  };

  const isGridView = (): boolean => {
    return typeof searchTerm !== 'undefined';
  };

  const shouldShowTabs = (): boolean => {
    return isGridView() && tabsToShow.length > 1 && params.large;
  };

  const getPanelBottomPills = (): PanelBottomPill[] => {
    if (props.bottomPills) {
      return props.bottomPills;
    }

    return getBottomPills(selectedTab, onTabClicked).filter((bottomPill) => {
      return tabsToShow.includes(bottomPill.id as StockPanelTabs);
    });
  };

  const getContent = (): ReactElement<any> => {
    return (
      <PanelContent
        ref={panelContentRef}
        className={`flexbox flex-v-row ${styles.panel} ${
          params.small
            ? `spacing-p-4 ${typeof searchTerm !== 'undefined' ? 'spacing-p-b-0' : ''}`
            : `spacing-p-t-8 spacing-p-r-12 spacing-p-l-12 ${typeof searchTerm !== 'undefined' ? 'spacing-p-b-0' : 'spacing-p-b-12'}`
        }`}
      >
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={isTextToImageTabActive() ? 'text-to-image-animation-container' : 'standard-animation-container'}
            initial={{height: 0, opacity: 0}}
            animate={{height: 'auto', opacity: 1}}
            transition={{duration: 0.3}}
            exit={{height: 'auto', opacity: 0}}
          >
            {getSearchBarContainer()}
          </motion.div>
        </AnimatePresence>
        {shouldShowTabs() ? getTabs() : null}
        {isGridView() ? getFiltersRow() : null}
        {getMainScreen()}
      </PanelContent>
    );
  };

  const isTextToImageTabActive = (): boolean => {
    return selectedTab === StockPanelTabs.TEXT_TO_IMAGE && typeof searchTerm !== 'undefined';
  };

  const getMainScreen = (): ReactElement<any> | null => {
    return isGridView() ? getStockGrid() : getCarousels();
  };

  const onCarouselStockReceived = (response: StockResponse): void => {
    setErrorState(TextToImageGridErrorState.NONE);
    const recentStockItemsData = carouselData.current[StockCarouselType.RECENTS] ? [...carouselData.current[StockCarouselType.RECENTS]] : undefined;
    const generatedImagesData = carouselData.current[StockCarouselType.TEXT_TO_IMAGE] ? [...carouselData.current[StockCarouselType.TEXT_TO_IMAGE]] : undefined;
    carouselData.current = {};
    carouselData.current[StockCarouselType.RECENTS] = recentStockItemsData;
    carouselData.current[StockCarouselType.TEXT_TO_IMAGE] = generatedImagesData;
    if (carouselData?.current) {
      response.stock.forEach((stockItem) => {
        switch (stockItem.type as StockSources) {
          case StockSources.PMW_ANIMATED_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForStickerItem(stockItem as PMWStockBackendAnimatedSticker),
            ];
            break;
          case StockSources.PMW_AI_ICON:
            carouselData.current[StockCarouselType.ICONS] = [
              ...(carouselData.current[StockCarouselType.ICONS] ?? []),
              getCarouselDataForAiIconItem(stockItem as PMWStockBackendAiIcon),
            ];
            break;
          case StockSources.PMW_ICON:
            carouselData.current[StockCarouselType.ICONS] = [
              ...(carouselData.current[StockCarouselType.ICONS] ?? []),
              getCarouselDataForIconItem(stockItem as PMWStockBackendIcon),
            ];
            break;
          case StockSources.PMW_EXTRACTED_GETTY_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForExtractedGettyStickerItem(stockItem as PMWStockBackendExtractedGettySticker),
            ];
            break;
          case StockSources.PMW_AI_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForAiStickerItem(stockItem as PMWStockBackendAiSticker),
            ];
            break;
          case StockSources.PMW_STILL_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForStillStickerItem(stockItem as PMWStockBackendStillSticker),
            ];
            break;
          case StockSources.PMW_SHAPE:
            carouselData.current[StockCarouselType.SHAPES] = [
              ...(carouselData.current[StockCarouselType.SHAPES] ?? []),
              getCarouselDataForShapeItem(stockItem as PMWStockBackendShape),
            ];
            break;
          case StockSources.GETTY_IMAGE:
          case StockSources.STORYBLOCK_IMAGE:
          case StockSources.PIXABY_IMAGE:
            carouselData.current[StockCarouselType.PHOTOS] = [
              ...(carouselData.current[StockCarouselType.PHOTOS] ?? []),
              getCarouselDataForImageItem(stockItem, stockItem.type as StockSources),
            ];
            break;
          case StockSources.GETTY_VIDEO:
          case StockSources.STORYBLOCK_VIDEO:
          case StockSources.PIXABY_VIDEO: {
            const carouselDataForVideo = getCarouselDataForVideoItem(stockItem, stockItem.type as StockSources);
            if (carouselDataForVideo) {
              carouselData.current[StockCarouselType.VIDEOS] = [...(carouselData.current[StockCarouselType.VIDEOS] ?? []), carouselDataForVideo];
            }
            break;
          }
          case StockSources.STORYBLOCK_ILLUSTRATION:
          case StockSources.PIXABY_ILLUSTRATION:
          case StockSources.GETTY_ILLUSTRATION: // no need to handle these sources since we don't show these in any Carousel
            break;
          case StockSources.STORYBLOCK_AUDIO:
            carouselData.current[StockCarouselType.AUDIOS] = [
              ...(carouselData.current[StockCarouselType.AUDIOS] ?? []),
              getCarouselDataForAudioItem(stockItem, getHeightForCarouselType(StockCarouselType.AUDIOS, params.small)),
            ];
            break;
          default:
            throw new Error(`Unhandled stock source: ${stockItem.type ?? ''}`);
        }
      });
      setLoaded(true);
    }
  };

  const onAudioCategoriesReceived = (response: AudioCategoryBackendData[]): void => {
    audioFilterIDs.current = [];
    stockTabsToFilterMapping.current[StockPanelTabs.AUDIOS] = response.map((responseRow) => {
      audioFilterIDs.current.push(responseRow.id);
      return getStockFilterForAudioFilterBackendData(responseRow);
    });
  };

  const loadAudioCategories = (): void => {
    getStockLibraryAudioCategories()
      .then(onAudioCategoriesReceived)
      .catch((e) => {
        console.error(e);
      });
  };

  const getCarousels = (): ReactElement => {
    return <ul className={`${styles.carouselList} spacing-m-t-6 flex-v-row`}>{getCarouselsForTabs()}</ul>;
  };

  const loadRecentStockItems = (): void => {
    getRecentStockItems()
      .then((res) => {
        carouselData.current[StockCarouselType.RECENTS] =
          res.length >= MIN_STOCK_ITEMS_TO_DISPLAY
            ? res.map((gridItem) => {
                return {
                  gridItemData: getGridItemForBackendData(gridItem),
                  width: gridItem.type === GRID_ITEM_TYPE.STOCK_AUDIO ? STOCK_AUDIO_GRID_ITEM_WIDTH : Number(gridItem.width ?? FALLBACK_GRID_ITEM_WIDTH),
                  height:
                    gridItem.type === GRID_ITEM_TYPE.STOCK_AUDIO
                      ? getHeightForCarouselType(StockCarouselType.RECENTS, params.small)
                      : Number(gridItem.height ?? FALLBACK_GRID_ITEM_HEIGHT),
                } as CarouselItemData;
              })
            : undefined;
        dispatch(setRecentStockAvailability(!!carouselData.current[StockCarouselType.RECENTS]));
        setRecentStockLoaded(true);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const loadGeneratedImages = (): void => {
    getGeneratedImagesForCarousel()
      .then((res) => {
        carouselData.current[StockCarouselType.TEXT_TO_IMAGE] = [
          AI_IMAGE_BUTTON_CAROUSEL_ITEM,
          ...res.map((aiGeneratedImage): CarouselItemData => {
            return {
              gridItemData: getGridItemForAIGeneratedImageV2(aiGeneratedImage),
              width: Number(aiGeneratedImage.imageProps.width),
              height: Number(aiGeneratedImage.imageProps.height),
            };
          }),
        ];
        setGeneratedImagesLoaded(true);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const onModalUnmount = (): void => {
    if (areFiltersVisible) {
      dispatch(toggleFiltersVisibility());
    }

    clearSelection();

    dispatch(deleteTabStateForId(tabId));
  };

  const trackMediaItemSearch = (mustHaveSubfilter?: boolean): void => {
    if (!searchTerm) {
      return;
    }
    const filterTerm = selectedTab === StockPanelTabs.STICKERS ? getGATrackingFilterTerm(selectedFilters) : '';
    if (mustHaveSubfilter && !filterTerm) {
      return;
    }
    trackPosterBuilderGA4Events(`MediaModal_${filterTerm + getGATrackingSearchTerm(selectedTab)}Search`, {
      [GA4EventParamName.SEARCH_TERM]: searchTerm,
    });
  };

  useEffect(() => {
    if (isMobile && selectedTab === StockPanelTabs.VIDEOS) {
      getHasUserSeenMobileVideoPreview()
        .then((hasSeen) => {
          if (!hasSeen) {
            dispatch(
              updatePanelData({
                panelId,
                data: {
                  [PanelDataKeys.GROWL]: {
                    icon: 'icon-tap-and-hold',
                    text: window.i18next.t('pmwjs_video_preview_message_growl'),
                  } as PanelGrowlData,
                },
              })
            );
            setTimeout(() => {
              dispatch(
                updatePanelData({
                  panelId,
                  data: {
                    [PanelDataKeys.GROWL]: undefined,
                  },
                })
              );
              void markUserHasSeenMobileVideoPreview();
            }, MOBILE_VIDEO_PREVIEW_GROWL_VISIBLE_DURATION);
          }
        })
        .catch((e: unknown) => {
          console.error(e);
        });
    }
    if (errorState !== TextToImageGridErrorState.NONE) {
      setErrorState(TextToImageGridErrorState.NONE);
    }
  }, [selectedTab]);

  useEffect(() => {
    const initialOffsets = getInitialOffsetsForInitialCarouselRequest(tabsToShow);
    for (const key in initialOffsets) {
      if (props.blacklistedSources?.includes(key as StockSources)) {
        delete initialOffsets[key as StockSources];
      }
    }
    getStockForCarousel({
      stockSrcToOffsetMap: initialOffsets,
    })
      .then(onCarouselStockReceived)
      .catch((e) => {
        console.error(e);
        setErrorState(TextToImageGridErrorState.GENERIC);
      });
  }, []);

  useEffect(() => {
    dispatch(setStockGetterOptions(getOptionsForStockGetter(selectedFilters, selectedTab, audioFilterIDs.current)));
  }, [selectedFilters, selectedTab, audioFilterIDs.current]);

  useEffect(() => {
    trackMediaItemSearch();
    toggleFiltersVisibilityIfChanged(selectedTab);
  }, [selectedTab, searchTerm]);

  useEffect(() => {
    if (selectedTab === StockPanelTabs.STICKERS && selectedFilters.length) {
      trackMediaItemSearch(true);
    }
  }, [selectedFilters]);

  useEffect(() => {
    trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_OPEN);
    dispatch(selectTab(tabId, props.defaultSearchTab ?? tabsToShow[0]));
    loadAudioCategories();
    loadRecentStockItems();
    if (showTextToImageTool) {
      loadGeneratedImages();
    }

    return () => {
      onModalUnmount();
    };
  }, [panelId]);

  return <Panel key={panelId} panelId={panelId} panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />;
}
